// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/* istanbul ignore file */
import {
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { type OrderStrategy } from '@shared/data-access/interfaces/redux/portfolio-rebalance-state';
import { AppIcon, Heading } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface OptionModalStrategies {
  key: string;
  description?: string;
}

interface ButtonChangeBenchmarkProps {
  isOpen: boolean;
  orderStrategy: OrderStrategy;
  handleModal: () => void;
  handleOrderStrategyChange: ({ target }: SelectChangeEvent) => void;
}

export const TaxSystemSelector = ({
  orderStrategy,
  handleModal,
  isOpen,
  handleOrderStrategyChange,
}: ButtonChangeBenchmarkProps) => {
  const { t } = useTranslation();

  const orderStrategies = [
    {
      key: 'FIFO',
      description: t('rebalance.order-strategies.fifo', { ns: 'rebalance' }),
    },
    {
      key: 'AVG',
      description: t('rebalance.order-strategies.avg', { ns: 'rebalance' }),
    },
    {
      key: 'BEST',
      description: t('rebalance.order-strategies.best', { ns: 'rebalance' }),
    },
  ];

  return (
    <div className="tax-systems flex" data-testId="tax-system-selector">
      <Heading
        variant="h4"
        title={`${t('rebalance.tax-system', {
          ns: 'rebalance',
        })}: ${orderStrategy}`}
        className="!text-2xl"
      />
      <button className="ml-2" onClick={handleModal} data-testid="edit-strategy">
        <AppIcon icon="Pen" color="secondary" size="sm" />
      </button>

      <Modal
        open={isOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') handleModal();
        }}
      >
        <div className="absolute left-1/2 top-1/2 w-[350px] -translate-x-2/4 -translate-y-2/4 rounded-md bg-page p-4">
          <h3 className="text-2xl font-semibold text-secondary-dark">
            <Translation i18nKey="rebalance.select-tax-system" ns="rebalance" />
          </h3>

          <FormControl
            variant="filled"
            sx={{ width: '100%', marginY: 3, borderBottom: 0 }}
            className="shadow-xl"
          >
            <InputLabel id="demo-simple-select-filled-label">
              <Translation i18nKey="rebalance.strategy" ns="rebalance" />
            </InputLabel>
            <Select
              value={orderStrategy}
              onChange={handleOrderStrategyChange}
              className="border-r-4"
              disableUnderline
              MenuProps={MenuProps}
              classes={{ root: '!h-14' }}
            >
              {orderStrategies.map(({ key, description }) => (
                <MenuItem key={key} value={key}>
                  {description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </Modal>
    </div>
  );
};
